import React, { useEffect, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import HistoryIcon from "@mui/icons-material/History";
import EditIcon from "@mui/icons-material/Edit";
import moment from "moment";
import PageContainer from "Common/Components/PageContainer/PageContainer";
import PageBreadcrumb from "Common/Components/PageBreadcrumb/PageBreadcrumb";
import CancelButton from "Common/Components/Buttons/CancelButton";
import LoadingSpinner from "components/LoadingSpinner";
import FormattedDate from "Common/Components/NameWithDate.tsx/FormattedDate";
import { MecordColors } from "Constants/Color";
import { RootState } from "../../../../index";
import { fetchAccessoryReturnData } from "slices/sales_order/challan/thunk";
import { clearChallanState } from "slices/sales_order/challan/reducer";
import { fetchGRNListForChallan } from "slices/Warehouse/ItemInward/ReturnedAccessories/thunk";
import { Axios } from "api/axios";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";

const statusLabel = (status?: string) => {
    if (status === "inwarded") return "Inwarded";
    if (status === "partially_inwarded") return "Partially Inwarded";
    return "Pending";
};

const ReturnAccessoriesView: React.FC = () => {
    document.title = "Accessory Return History";

    const { challanId } = useParams<{ challanId: string }>();
    const navigate = useNavigate();
    const dispatch = useDispatch<any>();
    const { accessoryReturnData, loading } = useSelector((state: RootState) => state.Challan);

    useEffect(() => {
        if (challanId) {
            dispatch(fetchAccessoryReturnData(challanId));
        }
        return () => {
            dispatch(clearChallanState());
        };
    }, [dispatch, challanId]);

    const [grnList, setGrnList] = useState<any[]>([]);

    useEffect(() => {
        if (!challanId) return;
        Axios.get(API_ENDPOINT.WAREHOUSE.ITEM_INWARD.RETURNED_ACCESSORIES_GRN.GRN_LIST(challanId))
            .then((res: any) => setGrnList(Array.isArray(res?.data?.data) ? res.data.data : []))
            .catch(() => {});
    }, [challanId]);

    const listingPath = accessoryReturnData?.sales_order_service_id
        ? `/sales_order/service/challan/${accessoryReturnData.sales_order_service_id}`
        : "/sales_order/service/sales_order_log";

    const thStyle: React.CSSProperties = {
        padding: "6px 8px",
        borderBottom: "2px solid #eee",
        textAlign: "left",
        fontSize: "0.78rem",
        fontWeight: 600,
        whiteSpace: "nowrap",
        backgroundColor: MecordColors.LightGrayColor,
    };
    const tdStyle: React.CSSProperties = {
        padding: "6px 8px",
        borderBottom: "1px solid #f0f0f0",
        fontSize: "0.78rem",
        verticalAlign: "middle",
    };

    if (loading && !accessoryReturnData) return <LoadingSpinner />;
    if (!accessoryReturnData) {
        return <div className="p-4 text-center">Challan not found.</div>;
    }

    const summary = accessoryReturnData.accessory_return_summary || {};
    const services = accessoryReturnData.services || [];
    const serviceById = services.reduce((acc: Record<string, any>, service: any) => {
        if (service.sales_order_service_item_id) {
            acc[service.sales_order_service_item_id] = service;
        }
        return acc;
    }, {});
    const returns = [...(accessoryReturnData.returned_accessories?.returns || [])].sort(
        (a: any, b: any) =>
            new Date(b.returned_at || 0).getTime() - new Date(a.returned_at || 0).getTime()
    );

    return (
        <PageContainer>
            <PageBreadcrumb
                title="Accessory Return History"
                titleLink=""
                breadcrumbs={[
                    { label: "Sales Order Services", to: "/sales_order/service/sales_order_log" },
                    { label: "Manage Challan", to: listingPath },
                    { label: "View Return", to: "" },
                ]}
                backNavigationPath={listingPath}
            />
            <hr />

            <Row>
                <Col lg={12}>
                    <Card className="shadow-none">
                        <Card.Header>
                            <div className="d-flex align-items-center gap-2">
                                <HistoryIcon style={{ color: MecordColors.ThemeBGColor }} />
                                <h6 className="card-title mb-0">Accessory return history</h6>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <Row className="mb-3">
                                <Col md={4}>
                                    <p className="mb-2">
                                        <strong>Challan No:</strong> {accessoryReturnData.challan_no || "-"}
                                    </p>
                                    <p className="mb-0">
                                        <strong>SO No:</strong> {accessoryReturnData.sales_order_number || "-"}
                                    </p>
                                </Col>
                                <Col md={4}>
                                    <p className="mb-2">
                                        <strong>Challan Date:</strong>{" "}
                                        <FormattedDate date={accessoryReturnData.challan_date} />
                                    </p>
                                    <p className="mb-0">
                                        <strong>Warehouse:</strong> {accessoryReturnData.warehouse_name || "-"}
                                    </p>
                                </Col>
                                <Col md={4}>
                                    <p className="mb-2">
                                        <strong>Issued Qty:</strong> {summary.issued_qty || 0}
                                    </p>
                                    <p className="mb-0">
                                        <strong>Returned Qty:</strong> {summary.returned_qty || 0}{" "}
                                        <span className="text-muted">
                                            (Remaining {summary.remaining_qty || 0})
                                        </span>
                                    </p>
                                </Col>
                            </Row>

                            {returns.length === 0 ? (
                                <div
                                    className="d-flex flex-column align-items-center justify-content-center"
                                    style={{ minHeight: 180, color: "#aaa" }}
                                >
                                    <p className="mb-0" style={{ fontSize: "0.88rem" }}>
                                        No accessory returns recorded yet.
                                    </p>
                                </div>
                            ) : (
                                returns.map((entry: any, index: number) => (
                                    <div
                                        key={entry.return_id || index}
                                        className="mb-4"
                                        style={{
                                            border: "1px solid #eee",
                                            borderRadius: 8,
                                            overflow: "hidden",
                                        }}
                                    >
                                        <div
                                            className="d-flex flex-wrap justify-content-between align-items-center gap-2"
                                            style={{
                                                backgroundColor: MecordColors.LightGrayColor,
                                                padding: "10px 12px",
                                            }}
                                        >
                                            <div>
                                                <strong>Return {returns.length - index}</strong>
                                                <span className="text-muted ms-2" style={{ fontSize: "0.8rem" }}>
                                                    {entry.returned_at && moment(entry.returned_at).isValid()
                                                        ? moment(entry.returned_at).format("DD-MMM-YYYY hh:mm A")
                                                        : "-"}
                                                </span>
                                            </div>
                                            <div className="d-flex align-items-center gap-2">
                                                <span
                                                    style={{
                                                        padding: "2px 8px",
                                                        borderRadius: 999,
                                                        backgroundColor: MecordColors.LightThemeBGColor,
                                                        color: MecordColors.ThemeBGColor,
                                                        fontSize: 11,
                                                        fontWeight: 700,
                                                    }}
                                                >
                                                    {statusLabel(entry.status)}
                                                </span>
                                                {entry.status === "pending_return" && (
                                                    <button
                                                        type="button"
                                                        title="Edit this return"
                                                        onClick={() =>
                                                            navigate(
                                                                `/sales_order/service/challan/return-edit/${challanId}/${entry.return_id}`
                                                            )
                                                        }
                                                        style={{
                                                            background: "none",
                                                            border: `1px solid ${MecordColors.ThemeBGColor}`,
                                                            borderRadius: 6,
                                                            color: MecordColors.ThemeBGColor,
                                                            padding: "2px 8px",
                                                            cursor: "pointer",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 4,
                                                            fontSize: 12,
                                                        }}
                                                    >
                                                        <EditIcon style={{ fontSize: 14 }} />
                                                        Edit
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <div style={{ padding: 12 }}>
                                            {entry.note ? (
                                                <p className="mb-3" style={{ fontSize: "0.82rem" }}>
                                                    <strong>Note:</strong> {entry.note}
                                                </p>
                                            ) : null}

                                            {(entry.items || []).map((item: any, itemIdx: number) => {
                                                const serviceObj = serviceById[item.sales_order_service_item_id] || {};
                                                return (
                                                    <div key={`${entry.return_id}-${itemIdx}`} className="mb-3">
                                                        <div
                                                            className="d-flex justify-content-between align-items-center mb-2"
                                                            style={{
                                                                backgroundColor: MecordColors.LightGrayColor,
                                                                padding: "8px 12px",
                                                                borderRadius: 6,
                                                            }}
                                                        >
                                                            <div>
                                                                <strong>{itemIdx + 1}. {item.product_name || serviceObj.product_name || "Service"}</strong>
                                                                <span className="text-muted ms-2">
                                                                    {item.product_sku || serviceObj.product_sku || ""}
                                                                    {serviceObj.serial_number ? ` | SN: ${serviceObj.serial_number}` : ""}
                                                                </span>
                                                            </div>
                                                            {serviceObj.service_category ? (
                                                                <span className="text-muted" style={{ fontSize: "0.78rem" }}>
                                                                    {serviceObj.service_category}
                                                                </span>
                                                            ) : null}
                                                        </div>
                                                        <div className="table-responsive">
                                                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                                                <thead>
                                                                    <tr>
                                                                        <th style={thStyle}>Accessory</th>
                                                                        <th style={thStyle}>SKU</th>
                                                                        <th style={{ ...thStyle, textAlign: "center" }}>Issued</th>
                                                                        <th style={{ ...thStyle, textAlign: "center" }}>Return Qty</th>
                                                                        <th style={{ ...thStyle, textAlign: "center" }}>Inwarded Qty</th>
                                                                        <th style={thStyle}>Serials</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {(item.accessories || []).map((acc: any, accIdx: number) => {
                                                                        const serviceAcc = (serviceObj.accessories || []).find(
                                                                            (a: any) =>
                                                                                (acc.accessory_id && a.accessory_id === acc.accessory_id) ||
                                                                                (acc.product_id && a.product_id === acc.product_id)
                                                                        );
                                                                        const issuedIds: string[] = Array.isArray(
                                                                            serviceAcc?.issued_inventory_product_ids
                                                                        )
                                                                            ? serviceAcc.issued_inventory_product_ids.map(String)
                                                                            : [];
                                                                        const issuedSerials: string[] = Array.isArray(
                                                                            serviceAcc?.serial_numbers
                                                                        )
                                                                            ? serviceAcc.serial_numbers.map((s: any) => String(s || ""))
                                                                            : [];
                                                                        const selectedIds: string[] = (
                                                                            Array.isArray(acc.return_inventory_product_ids)
                                                                                ? acc.return_inventory_product_ids
                                                                                : Array.isArray(acc.issued_inventory_product_ids)
                                                                                  ? acc.issued_inventory_product_ids
                                                                                  : []
                                                                        )
                                                                            .filter(Boolean)
                                                                            .map(String)
                                                                            .slice(0, Number(acc.return_qty || 0) || undefined);
                                                                        const serialLabels = selectedIds.map((id) => {
                                                                            const idx = issuedIds.indexOf(id);
                                                                            return idx >= 0 && issuedSerials[idx]
                                                                                ? issuedSerials[idx]
                                                                                : id.slice(0, 8);
                                                                        });
                                                                        return (
                                                                        <tr
                                                                            key={`${item.sales_order_service_item_id}-${acc.accessory_id || accIdx}`}
                                                                            style={{ backgroundColor: MecordColors.ActiveBackground }}
                                                                        >
                                                                            <td style={tdStyle}>{acc.product_name || "-"}</td>
                                                                            <td style={tdStyle}>{acc.product_sku || "-"}</td>
                                                                            <td style={{ ...tdStyle, textAlign: "center" }}>
                                                                                {acc.issued_qty || 0}
                                                                            </td>
                                                                            <td style={{ ...tdStyle, textAlign: "center" }}>
                                                                                {acc.return_qty || 0}
                                                                            </td>
                                                                            <td style={{ ...tdStyle, textAlign: "center" }}>
                                                                                {acc.inwarded_qty || 0}
                                                                            </td>
                                                                            <td style={tdStyle}>
                                                                                {serialLabels.length
                                                                                    ? serialLabels.join(", ")
                                                                                    : "—"}
                                                                            </td>
                                                                        </tr>
                                                                        );
                                                                    })}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))
                            )}

                            {/* GRN History */}
                            {grnList.length > 0 && (
                                <div className="mt-4">
                                    <h6 className="mb-2" style={{ fontWeight: 600, fontSize: "0.9rem", borderBottom: "1px solid #eee", paddingBottom: 6 }}>
                                        GRN History
                                    </h6>
                                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                        <thead>
                                            <tr>
                                                {["GRN No.", "Inward Date", "Received By", "Warehouse", ""].map((h) => (
                                                    <th key={h} style={{ ...thStyle, fontSize: "0.75rem" }}>{h}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {grnList.map((grn: any) => (
                                                <tr key={grn.inward_id}>
                                                    <td style={tdStyle}>
                                                        <span style={{ fontWeight: 600, color: MecordColors.ThemeBGColor }}>
                                                            {grn.ragrn_no}
                                                        </span>
                                                    </td>
                                                    <td style={tdStyle}>{grn.received_date ? <FormattedDate date={grn.received_date} /> : "—"}</td>
                                                    <td style={tdStyle}>{grn.received_by || "—"}</td>
                                                    <td style={tdStyle}>{grn.warehouse?.warehouse_name || "—"}</td>
                                                    <td style={tdStyle}>
                                                        <button
                                                            onClick={() => navigate(`/warehouse/item-inward/returned-accessories/grn/${grn.inward_id}`)}
                                                            style={{
                                                                background: "none",
                                                                border: `1px solid ${MecordColors.ThemeBGColor}`,
                                                                borderRadius: 4,
                                                                color: MecordColors.ThemeBGColor,
                                                                padding: "2px 10px",
                                                                cursor: "pointer",
                                                                fontSize: 12,
                                                            }}
                                                        >
                                                            View
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            <div className="d-flex justify-content-center mt-3">
                                <CancelButton
                                    label="Back"
                                    onClick={() => navigate(listingPath)}
                                    showConfirmation={false}
                                />
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </PageContainer>
    );
};

export default ReturnAccessoriesView;
